import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Stack,
  Divider,
  Chip,
  Paper,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SettingsIcon from "@mui/icons-material/Settings";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";
import { toast } from "react-toastify";

import { getDeliveryTimesRequest } from "services/modules/lookups/deliveryTimes";
import {
  getDeliveryDaySlotsRequest,
  updateDeliveryDaySlotsRequest,
  getDeliveryTimeSlotDaysRequest,
  updateDeliveryTimeSlotDaysRequest,
} from "services/modules/lookups/deliveryDaySlots";

function DeliverySchedulePage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const isRtl = i18n.dir() === "rtl";

  const [activeTab, setActiveTab] = useState(0);

  // Selectors
  const deliveryTimes = useSelector((state) => state.deliveryTimes.deliveryTimes) || [];
  const scheduleByDay = useSelector((state) => state.deliveryDaySlots.scheduleByDay) || {};
  const daysBySlot = useSelector((state) => state.deliveryDaySlots.daysBySlot) || {};
  const load = useSelector((state) => state.deliveryDaySlots.load);
  const deliveryTimesLoad = useSelector((state) => state.deliveryTimes.load);

  // Fetch initial data
  useEffect(() => {
    dispatch(getDeliveryTimesRequest({ params: { page_size: 100 } }));
    for (let i = 0; i <= 6; i++) {
      dispatch(getDeliveryDaySlotsRequest({ day_of_week: i }));
    }
  }, [dispatch]);

  // Fetch slot days when switching to Tab 1
  useEffect(() => {
    if (activeTab === 1 && deliveryTimes.length > 0) {
      deliveryTimes.forEach((slot) => {
        dispatch(getDeliveryTimeSlotDaysRequest({ delivery_time_id: slot.id }));
      });
    }
  }, [activeTab, deliveryTimes, dispatch]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Breadcrumbs
  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("common.home"),
    },
    {
      active: true,
      path: "/delivery-times",
      title: t("sidebar.delivery_times"),
    },
    {
      active: false,
      title: t("sidebar.delivery_schedule"),
    },
  ];

  // Helper for day names translation
  const getDayName = (dayIndex) => {
    const days = {
      0: t("days.0", "Sunday"),
      1: t("days.1", "Monday"),
      2: t("days.2", "Tuesday"),
      3: t("days.3", "Wednesday"),
      4: t("days.4", "Thursday"),
      5: t("days.5", "Friday"),
      6: t("days.6", "Saturday"),
    };
    return days[dayIndex];
  };

  // Toggle delivery slot for a specific day
  const handleToggleDaySlot = (dayIndex, slotId, isEnabled) => {
    const currentSlotIds = scheduleByDay[dayIndex] || [];
    let newSlotIds;
    if (isEnabled) {
      // Remove
      newSlotIds = currentSlotIds.filter((id) => id !== slotId);
    } else {
      // Add
      newSlotIds = [...currentSlotIds, slotId];
    }

    dispatch(
      updateDeliveryDaySlotsRequest({
        body: {
          day_of_week: dayIndex,
          delivery_time_ids: newSlotIds,
        },
        action: () => {
          toast.success(t("delivery_schedule.save_success", "Delivery schedule updated successfully!"));
          dispatch(getDeliveryDaySlotsRequest({ day_of_week: dayIndex }));
          // If daysBySlot has the slot loaded, fetch it too to synchronize Tab 2
          if (daysBySlot[slotId]) {
            dispatch(getDeliveryTimeSlotDaysRequest({ delivery_time_id: slotId }));
          }
        },
      })
    );
  };

  // Toggle day for a specific slot
  const handleToggleSlotDay = (slotId, dayIndex, isEnabled) => {
    const currentDays = daysBySlot[slotId] || [];
    let newDays;
    if (isEnabled) {
      // Remove
      newDays = currentDays.filter((d) => d !== dayIndex);
    } else {
      // Add
      newDays = [...currentDays, dayIndex];
    }

    dispatch(
      updateDeliveryTimeSlotDaysRequest({
        delivery_time_id: slotId,
        body: {
          day_of_weeks: newDays,
        },
        action: () => {
          toast.success(t("delivery_schedule.save_success", "Delivery schedule updated successfully!"));
          dispatch(getDeliveryTimeSlotDaysRequest({ delivery_time_id: slotId }));
          // Synchronize Tab 1 by reloading the updated day
          dispatch(getDeliveryDaySlotsRequest({ day_of_week: dayIndex }));
        },
      })
    );
  };

  const getSlotName = (slot) => {
    return (
      slot.localized_data?.[i18n.language]?.description ||
      slot.localized_data?.en?.description ||
      slot.description_en ||
      slot.name ||
      `${slot.start_time} - ${slot.end_time}`
    );
  };

  const isUpdating = load?.loading && load?.type === "update_day_slots";
  const isUpdatingSlotDays = load?.loading && load?.type === "update_slot_days";
  const isLoadingData =
    (deliveryTimesLoad?.loading && deliveryTimesLoad?.type === "list") ||
    (load?.loading && load?.type === "list_day_slots");

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <Loader open={isUpdating || isUpdatingSlotDays || isLoadingData} />

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Breadcrumb data={breadcrumbs} />
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(245,247,250,0.9) 100%)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" gap={2}>
          <Box>
            <Typography variant="h4" fontWeight="700" color="text.primary" sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SettingsIcon color="primary" sx={{ fontSize: 32 }} />
              {t("delivery_schedule.title", "Delivery Schedule by Day")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Configure delivery availability timespans for each day of the week.
            </Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              backgroundColor: "background.paper",
              borderRadius: "12px",
              p: 0.5,
              boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
              border: "1px solid",
              borderColor: "divider",
              "& .MuiTabs-indicator": {
                borderRadius: "10px",
                height: "100%",
                opacity: 0.15,
                backgroundColor: "primary.main",
              },
            }}
          >
            <Tab
              label={
                <Stack direction="row" alignItems="center" gap={1}>
                  <CalendarMonthIcon sx={{ fontSize: 18 }} />
                  <span>{t("delivery_schedule.tab_by_day", "Configure by Day")}</span>
                </Stack>
              }
              sx={{
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "10px",
                minHeight: "44px",
                py: 1,
                px: 2.5,
              }}
            />
            <Tab
              label={
                <Stack direction="row" alignItems="center" gap={1}>
                  <AccessTimeIcon sx={{ fontSize: 18 }} />
                  <span>{t("delivery_schedule.tab_by_slot", "Configure by Slot")}</span>
                </Stack>
              }
              sx={{
                fontWeight: "600",
                textTransform: "none",
                borderRadius: "10px",
                minHeight: "44px",
                py: 1,
                px: 2.5,
              }}
            />
          </Tabs>
        </Stack>
      </Paper>

      {/* Tab 0: Configure by Day */}
      {activeTab === 0 && (
        <Grid container >
          {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
            const activeSlots = scheduleByDay[dayIndex] || [];
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} padding={1} key={dayIndex}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 24px rgba(0,0,0,0.06)",
                      borderColor: "primary.light",
                    },
                  }}
                >
                  <CardHeader
                    title={
                      <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {getDayName(dayIndex)}
                      </Typography>
                    }
                    action={
                      <Chip
                        label={`${activeSlots.length} ${t("delivery_schedule.slots", "Slots")}`}
                        color={activeSlots.length > 0 ? "primary" : "default"}
                        size="small"
                        sx={{ fontWeight: "600" }}
                      />
                    }
                    sx={{ pb: 1, borderBottom: "1px solid", borderColor: "divider" }}
                  />
                  <CardContent sx={{ pt: 2 }}>
                    {deliveryTimes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                        No predefined slots found.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {deliveryTimes.map((slot) => {
                          const isEnabled = activeSlots.includes(slot.id);
                          return (
                            <Box
                              key={slot.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 1,
                                px: 1.5,
                                borderRadius: "8px",
                                backgroundColor: isEnabled ? "action.hover" : "transparent",
                                border: "1px solid",
                                borderColor: isEnabled ? "primary.lighter" : "transparent",
                                transition: "all 0.2s",
                              }}
                            >
                              <Typography variant="body2" fontWeight="500">
                                {getSlotName(slot)}
                              </Typography>
                              <Switch
                                size="small"
                                checked={isEnabled}
                                onChange={() => handleToggleDaySlot(dayIndex, slot.id, isEnabled)}
                                inputProps={{ "aria-label": `Toggle ${getSlotName(slot)} on ${getDayName(dayIndex)}` }}
                              />
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Tab 1: Configure by Predefined Slot */}
      {activeTab === 1 && (
        <Stack spacing={3}>
          {deliveryTimes.map((slot) => {
            const enabledDays = daysBySlot[slot.id] || [];
            return (
              <Card
                key={slot.id}
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "border-color 0.2s",
                  "&:hover": {
                    borderColor: "primary.light",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={3}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
                        <AccessTimeIcon color="action" />
                        {getSlotName(slot)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Select the days of the week when this delivery slot is active.
                      </Typography>
                    </Box>

                    <Stack direction="row" flexWrap="wrap" gap={1.5}>
                      {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => {
                        const isEnabled = enabledDays.includes(dayIndex);
                        return (
                          <Chip
                            key={dayIndex}
                            label={getDayName(dayIndex)}
                            color={isEnabled ? "primary" : "default"}
                            variant={isEnabled ? "filled" : "outlined"}
                            onClick={() => handleToggleSlotDay(slot.id, dayIndex, isEnabled)}
                            sx={{
                              fontWeight: "600",
                              px: 1,
                              py: 2,
                              borderRadius: "10px",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "scale(1.05)",
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          {deliveryTimes.length === 0 && (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: "16px" }}>
              <Typography color="text.secondary">No predefined delivery slots available.</Typography>
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
}

export default DeliverySchedulePage;
